/**
 * CreatorProfileDrawer — the thread view's inline creator-profile panel
 * (Figma: the thread column shrinks to make room, no overlay). Sections
 * follow Figma's order: header card, Audience, Outreach, Country and time
 * zone (not in Figma; owner ruling 2026-10-11), Contact details, Rates, Files
 * received, Actions.
 *
 * Skipped against Figma, all for lack of backing data (not oversights):
 * - A niche/category tag on the header card — `OutreachRow` has no such field.
 * - "Follow-ups: N sent" — no sent-count field, only `nextFollowupAt`.
 * - "Opens" — the row carries no open count.
 * - Phone / Instagram contact cards — the row carries emails only.
 * - Per-file "Applied"/"Needs review" status badges — `AttachmentSummary`
 *   carries no status field.
 * - The Rates section's per-deliverable, agent-assisted lock/unlock grid —
 *   the BE's `AgreeRate` is one atomic call for a whole rates array, no
 *   per-deliverable lock/unlock, no agent auto-lock, no reversibility. A
 *   different feature that doesn't exist yet, not a small gap like the
 *   others — kept at today's agree-once fidelity instead of faking it.
 */
import { useRef, useState, type ReactNode } from 'react';
import { followupPauseAction } from './followupPauseUi';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { OutreachContactPatch, OutreachRow, OutreachRowEmail } from '@discovr/types';
import { isWithBrandStatus } from '@discovr/types';
import { fetchOutreachAttachments, downloadOutreachAttachment } from '@discovr/features';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  Lock,
  FilePdf,
  FileImage,
  FileXls,
  FileDoc,
  FileText,
  DownloadSimple,
  ChartBar,
  PaperPlaneTilt,
  AddressBook,
  EnvelopeSimple,
  Receipt,
  ArrowLineUp,
  Play,
  Clock,
  MagnifyingGlass,
  X,
  CaretLeft,
  CaretRight,
} from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { fmtDate } from '../../utils/campaignDetailHelpers';
import { useNow } from '../../hooks';
import { avatarInitials } from './avatarInitials';
import { ContactEditPopover } from './ContactEditPopover';
import { CreatorGeoSection } from './CreatorGeoSection';
import { InMessageTag } from './InMessageTag';
import { PILL_BASE } from './OutreachStatusPill';
import { PILL_GREEN } from './pillPalette';
import { sourceLabel } from './emailSources';
import { FOCUS_RING } from './StatusTooltip';
import { fmtAgreedItem, fmtAgreedTotalLocked } from './agreedMoney';
import { formatBytes } from './attachmentFormat';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { canAgreeRate, canMoveToBrand, hasRateToHandOff } from './handoffStates';
import { isSequenceEnded, nextFollowupDate, overdueLabel, SEQUENCE_ENDED_LABEL } from './rowStateLabels';
import { CreatorLookupBadge } from '../CreatorsTab/CreatorLookupBadge';
import { resolveCreatorLookupBadge } from '../CreatorsTab/creatorLookupStatus';
import { resolveMetricsCaption, TONE_CLASS } from '../CreatorsTab/creatorMetricsStatus';
import { contentMetricsCopy } from '../CreatorsTab/contentMetricsCopy';

export interface CreatorProfileDrawerProps {
  open: boolean;
  row: OutreachRow | null;
  canWrite: boolean;
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch (default ON); OFF leaves "Next follow-up" empty. */
  autoFollowups?: boolean;
  onSave: (patch: OutreachContactPatch) => Promise<void>;
  onClose: () => void;
  onAgreeRate?: () => void;
  onSendToBrand?: () => void;
  onPauseFollowups?: () => void;
  onResumeFollowups?: () => void;
}

const SECTION_LABEL = 'flex items-center gap-2 text-caption-medium text-muted-foreground uppercase';
const SECTION_CARD = 'flex flex-col gap-4 rounded-xl border border-border bg-card p-5';
// globals.css's unlayered border-color beats a layered utility, hence the `!`.
const ROW_DIVIDER = 'border-b border-b-(--border-grey-subtle)! last:border-b-0';
const ACTION_ROW =
  `flex w-full items-center gap-2.5 rounded-md py-2.5 text-left text-body-sm-regular text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`;
// Same cadence as the Creators tab's own metrics-caption clock (CreatorsTab.tsx's useNow call).
const METRICS_CAPTION_TICK_MS = 300_000;

function AudienceStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-(--border-grey-subtle)! bg-(--background-primary-light) p-4">
      <p className="text-caption-medium text-muted-foreground uppercase">{label}</p>
      <p className="text-heading-h6 text-foreground tabular-nums">{value}</p>
    </div>
  );
}

const countLabel = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

function KeyValue({ label, last, children }: { label: string; last?: boolean; children: ReactNode }) {
  return (
    <div className={cn('flex items-center justify-between gap-3 py-2', !last && ROW_DIVIDER)}>
      <dt className="text-body-sm-regular text-(--foreground-secondary-default)">{label}</dt>
      <dd className="text-body-sm-medium text-foreground">{children}</dd>
    </div>
  );
}

const OVERRIDE_CAPTION = 'Email · This campaign only';

function emailCaption(email: OutreachRowEmail): string {
  const detail = email.primary ? 'Primary' : email.source ? sourceLabel(email.source) : '';
  return detail ? `Email · ${detail}` : 'Email';
}

// The composer sends to the override when one is set, so it leads the strip; cards dedupe by address.
function contactCards(row: OutreachRow): { address: string; caption: string }[] {
  const cards: { address: string; caption: string }[] = [];
  const seen = new Set<string>();
  const add = (address: string, caption: string) => {
    const key = address.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    cards.push({ address, caption });
  };
  const emails = row.emails ?? [];
  const override = row.emailOverride;
  if (override && !emails.some((e) => e.address.toLowerCase() === override.toLowerCase())) {
    add(override, OVERRIDE_CAPTION);
  }
  for (const e of emails) add(e.address, emailCaption(e));
  return cards;
}

const CARD_STEP = 308;

const isGmailAddress = (address: string) => /@(gmail|googlemail)\.com$/i.test(address.trim());

/** Figma's contact strip: one card per address, scrolled by the arrows; Manage opens the editor. */
function ContactDetailsSection({ row, canWrite, onSave }: Pick<CreatorProfileDrawerProps, 'canWrite' | 'onSave'> & { row: OutreachRow }) {
  // No address yet: open on the editor, so Add email (composer, contact cell) lands straight on the field.
  const [managing, setManaging] = useState(() => !(row.emails ?? []).length);
  // Once opened the editor stays mounted (hidden when collapsed) so Done doesn't discard typed edits.
  const [editorOpened, setEditorOpened] = useState(managing);
  const stripRef = useRef<HTMLDivElement>(null);
  const cards = contactCards(row);
  const scroll = (dir: 1 | -1) => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    stripRef.current?.scrollBy({ left: dir * CARD_STEP, behavior: reduced ? 'auto' : 'smooth' });
  };
  const toggleManaging = () => {
    setManaging((m) => !m);
    setEditorOpened(true);
  };
  return (
    <div className={SECTION_CARD}>
      <div className="flex items-center justify-between gap-2">
        <span className={SECTION_LABEL}>
          <AddressBook size={16} /> Contact details
        </span>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button type="button" variant="outline" size="xs" aria-expanded={managing} onClick={toggleManaging}>
              {managing ? 'Done' : 'Manage'}
            </Button>
          )}
          {cards.length > 1 && (
            <div className="flex items-center">
              <Button type="button" variant="ghost" size="icon-xs" aria-label="Previous contact" onClick={() => scroll(-1)}>
                <CaretLeft size={16} />
              </Button>
              <Button type="button" variant="ghost" size="icon-xs" aria-label="Next contact" onClick={() => scroll(1)}>
                <CaretRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
      {cards.length > 0 ? (
        <div ref={stripRef} className="-mx-5 flex scroll-pl-5 snap-x gap-2 overflow-x-auto px-5 scrollbar-none">
          {cards.map((card) => (
            <div key={card.address.toLowerCase()} className="flex w-[300px] shrink-0 snap-start items-center gap-3 rounded-xl border border-border p-3">
              {isGmailAddress(card.address) ? (
                <img src={gmailLogo} alt="" className="size-6 shrink-0 object-contain" />
              ) : (
                <EnvelopeSimple size={24} aria-hidden className="shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <p className="truncate text-body-sm-medium text-foreground" title={card.address}>{card.address}</p>
                <p className="truncate text-caption-regular text-muted-foreground">{card.caption}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-body-sm-regular text-muted-foreground">No email on file</p>
      )}
      {canWrite && editorOpened && (
        <div hidden={!managing}>
          <ContactEditPopover
            row={row}
            onSave={async (patch) => {
              await onSave(patch);
              setManaging(false);
            }}
            onClose={() => setManaging(false)}
          />
        </div>
      )}
    </div>
  );
}


export function CreatorProfileDrawer({
  open,
  row,
  canWrite,
  campaignZone,
  autoFollowups = true,
  onSave,
  onClose,
  onAgreeRate,
  onSendToBrand,
  onPauseFollowups,
  onResumeFollowups,
}: CreatorProfileDrawerProps) {
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);

  // A different creator clears any unlock-confirm left open from the
  // previous one (e.g. ThreadHeader's Prev/Next swaps `row` in place while
  // the drawer stays mounted) — same render-phase reset pattern as
  // ThreadView's composer seed.
  const [prevEntryId, setPrevEntryId] = useState(row?.entryId);
  if (row?.entryId !== prevEntryId) {
    setPrevEntryId(row?.entryId);
    setUnlockConfirmOpen(false);
  }

  const now = useNow(METRICS_CAPTION_TICK_MS);
  if (!open || !row) return null;

  const nextFollowupOn = nextFollowupDate(row, autoFollowups);
  const nextFollowupOverdue = nextFollowupOn ? overdueLabel(nextFollowupOn, new Date(now)) : null;
  const isAgreed = hasRateToHandOff(row) && !!row.agreedDeliverables?.length;
  const withBrand = isWithBrandStatus(row.status);
  const hasLockedRate = (hasRateToHandOff(row) || withBrand) && !!row.agreedDeliverables?.length;
  const lockedAmount = isAgreed ? fmtAgreedTotalLocked(row.agreedMoney) : null;
  const pauseAction = followupPauseAction(row);
  const paused = pauseAction === 'resume';
  const canSendToBrand = canWrite && canMoveToBrand(row);
  const canPauseFollowups = canWrite && pauseAction !== null;
  const lookupBadge = resolveCreatorLookupBadge(row.lookupStatus, { isAdmin: true });
  // avg_views/engagement_rate and metrics_pending_since matter here, not just
  // metrics_status: without them a manual-equivalent legacy row would read as
  // unfetched, and a stalled pending row would read "Fetching…" forever
  // instead of "Fetch stalled" — exactly the caption the Creators tab shows.
  const metricsCaption = row.metricsStatus
    ? resolveMetricsCaption(
        {
          metrics_status: row.metricsStatus,
          avg_views: row.avgViews ?? 0,
          engagement_rate: row.engagementRate ?? 0,
          metrics_pending_since: row.metricsPendingSince,
        },
        row.lookupStatus,
        now,
        contentMetricsCopy(row.platform),
      )
    : null;

  return (
    // min-h-0 lets this flex item shrink below its content's natural height —
    // without it the flexbox default (min-height: auto) blocks the body's
    // overflow-y-auto from ever kicking in. `h-full` so the panel takes its
    // host's height whether that host is the thread row or a Sheet column.
    // Figma 505:50397: a 540px panel, a #fafafa body and white 20px-padded cards.
    <aside className="flex size-full min-h-0 w-full max-w-[540px] min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <h2 className="text-body-sm-medium text-foreground">Creator profile</h2>
        {/* Matches the Script/Content review drawers' close-button convention
            (see insights/DrawerHeader.tsx). */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-xl bg-(--background-primary-light) p-4">
      <div className={SECTION_CARD}>
        <div className="flex items-center gap-4">
          <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-muted text-body-sm-medium text-muted-foreground">
            {row.avatarUrl ? (
              <img src={row.avatarUrl} alt="" className="size-14 rounded-full object-cover" />
            ) : (
              avatarInitials(row.displayName || row.username)
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-heading-h5 text-foreground">{row.displayName || row.username}</p>
            <p className="truncate text-body-sm-regular text-(--foreground-secondary-default)">@{row.username}</p>
            {(lookupBadge.pill || metricsCaption) && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <CreatorLookupBadge pill={lookupBadge.pill} />
                {metricsCaption && <span className={`text-[11px] ${TONE_CLASS[metricsCaption.tone]}`}>{metricsCaption.label}</span>}
              </div>
            )}
          </div>
        </div>
        {isAgreed && (
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn(PILL_BASE, PILL_GREEN)}>
              <Lock size={12} /> Rate locked
            </span>
            {lockedAmount && <span className={cn(PILL_BASE, PILL_GREEN)}>{lockedAmount}</span>}
          </div>
        )}
      </div>

      <div className={SECTION_CARD}>
        <span className={SECTION_LABEL}>
          <ChartBar size={16} /> Audience
        </span>
        <div className="grid grid-cols-3 gap-2">
          {row.followersHidden && !row.followers
            ? <AudienceStat label="Followers" value="Hidden" />
            : row.followers != null && <AudienceStat label="Followers" value={countLabel(row.followers)} />}
          {row.engagementRate != null && <AudienceStat label="ER" value={`${row.engagementRate.toFixed(2)}%`} />}
          {row.avgViews != null && <AudienceStat label="Avg views" value={countLabel(row.avgViews)} />}
        </div>
      </div>

      <div className={cn(SECTION_CARD, 'gap-3')}>
        <span className={SECTION_LABEL}>
          <PaperPlaneTilt size={16} /> Outreach
        </span>
        <dl>
          <KeyValue label="Reached on">
            <img src={gmailLogo} alt="Email" className="h-4 w-auto" />
          </KeyValue>
          <KeyValue label="Last sent">{row.lastMessage?.sentAt ? fmtDate(row.lastMessage.sentAt) : '—'}</KeyValue>
          <KeyValue label="Next follow-up" last>
            {paused ? 'Paused' : nextFollowupOn ? (nextFollowupOverdue ? `${fmtDate(nextFollowupOn)} · ${nextFollowupOverdue}` : fmtDate(nextFollowupOn)) : isSequenceEnded(row) ? SEQUENCE_ENDED_LABEL : '—'}
          </KeyValue>
        </dl>
      </div>

      <div className={SECTION_CARD}>
        <CreatorGeoSection userId={row.userId} platform={row.platform} campaignZone={campaignZone} />
      </div>

      <ContactDetailsSection key={row.entryId} row={row} canWrite={canWrite} onSave={onSave} />

      <div className={cn(SECTION_CARD, 'gap-3')}>
        <div className="flex items-center justify-between gap-2">
          <span className={SECTION_LABEL}>
            <Receipt size={16} /> Rates
          </span>
          {hasLockedRate && (
            <span className="text-[12px] text-muted-foreground tabular-nums">
              {withBrand ? 'Held by the brand' : `${row.agreedDeliverables?.length} locked`}
            </span>
          )}
        </div>
        {hasLockedRate ? (
          <div className="space-y-3">
            <ul>
              {row.agreedDeliverables?.map((d, index) => (
                <li key={`${d.type}-${index}`} className={cn('flex min-h-[57px] items-center justify-between gap-3 py-2', ROW_DIVIDER)}>
                  <span className="text-body-sm-regular text-(--foreground-secondary-default) capitalize">{d.type.replace(/_/g, ' ')}</span>
                  <span className={cn(PILL_BASE, PILL_GREEN, 'tabular-nums')}>
                    <Lock size={12} aria-hidden="true" />
                    <span className="sr-only">Locked at</span>
                    {fmtAgreedItem(d)}
                  </span>
                </li>
              ))}
            </ul>
            {isAgreed && (
              <Button type="button" variant="outline" size="sm" onClick={() => setUnlockConfirmOpen(true)}>
                Unlock rate
              </Button>
            )}
            <p className="text-[12px] text-muted-foreground">
              {withBrand ? 'With the brand · Re-agree from the thread to revise' : 'Locked from the outreach agreement · Re-agree from the thread to change'}
            </p>
          </div>
        ) : canWrite && canAgreeRate(row) ? (
          <div className="space-y-3">
            <p className="text-body-sm-regular text-muted-foreground">For rates agreed by call or WhatsApp, outside the email thread.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => onAgreeRate?.()}>
              Add rates
            </Button>
          </div>
        ) : null}
      </div>

      <FilesReceivedSection entryId={row.entryId} />

      {(canSendToBrand || canPauseFollowups) && (
        <div className={cn(SECTION_CARD, 'gap-3')}>
          <span className={SECTION_LABEL}>Actions</span>
          <div className="flex flex-col">
            {canSendToBrand && (
              <button
                type="button"
                className={ACTION_ROW}
                disabled={!hasRateToHandOff(row)}
                title={!hasRateToHandOff(row) ? 'Agree a rate first' : undefined}
                onClick={() => onSendToBrand?.()}
              >
                <ArrowLineUp size={18} />
                Move to brand dashboard
              </button>
            )}
            {canPauseFollowups && (
              <button type="button" className={ACTION_ROW} onClick={() => (paused ? onResumeFollowups?.() : onPauseFollowups?.())}>
                {paused ? <Play size={18} /> : <Clock size={18} />}
                {paused ? 'Resume follow-ups' : 'Pause follow-ups'}
              </button>
            )}
          </div>
        </div>
      )}
      </div>

      <Dialog open={unlockConfirmOpen} onOpenChange={setUnlockConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock this rate?</DialogTitle>
            <DialogDescription>
              {row.displayName || row.username}
              {lockedAmount ? ` · ${lockedAmount}` : ''}. The creator is not notified — this only changes what
              we hold on file.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setUnlockConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setUnlockConfirmOpen(false);
                onAgreeRate?.();
              }}
            >
              Unlock
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

function FilesReceivedSection({ entryId }: { entryId: string }) {
  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['outreach-attachments', entryId],
    queryFn: () => fetchOutreachAttachments(entryId),
  });
  const download = useMutation({
    mutationFn: ({ id, filename }: { id: number; filename: string }) => downloadOutreachAttachment(entryId, id, filename),
    onError: () => toast.error("Couldn't download this file — try again"),
  });

  const filtered = (data ?? []).filter((a) => a.filename.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className={cn(SECTION_CARD, 'gap-3')}>
      <div className="flex items-center justify-between">
        <span className={SECTION_LABEL}>
          <FileText size={16} /> Files received
        </span>
        <div className="flex items-center gap-2">
          {!!data?.length && <span className="text-caption-regular text-muted-foreground">{data.length}</span>}
          {!!data?.length && (
            <Button type="button" variant="outline" size="xs" onClick={() => setSeeAllOpen(true)}>
              See all
            </Button>
          )}
        </div>
      </div>
      {isLoading ? (
        <p className="text-body-sm-regular text-muted-foreground">Loading…</p>
      ) : !data?.length ? (
        <p className="text-body-sm-regular text-muted-foreground">No files received yet</p>
      ) : (
        <ul>
          {data.slice(0, 2).map((a) => (
            <FileRow key={a.id} filename={a.filename} contentType={a.contentType} sizeBytes={a.sizeBytes} receivedAt={a.receivedAt} isInline={a.isInline} onDownload={() => download.mutate({ id: a.id, filename: a.filename })} disabled={download.isPending} />
          ))}
        </ul>
      )}

      <Dialog open={seeAllOpen} onOpenChange={setSeeAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Files received</DialogTitle>
            <DialogDescription>Everything this creator has sent on this campaign.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search files" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-8" />
          </div>
          <ul className="space-y-2">
            {filtered.map((a) => (
              <FileRow key={a.id} filename={a.filename} contentType={a.contentType} sizeBytes={a.sizeBytes} receivedAt={a.receivedAt} isInline={a.isInline} onDownload={() => download.mutate({ id: a.id, filename: a.filename })} disabled={download.isPending} />
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FileRow({
  filename,
  contentType,
  sizeBytes,
  receivedAt,
  isInline,
  onDownload,
  disabled,
}: {
  filename: string;
  contentType: string;
  sizeBytes: number;
  receivedAt: string;
  isInline?: boolean;
  onDownload: () => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-(--background-primary-light) text-muted-foreground">
        <FileTypeIcon contentType={contentType} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-body-sm-medium text-foreground">{filename}</span>
          {isInline ? <InMessageTag /> : null}
        </span>
        <span className="block text-caption-regular text-muted-foreground">{`${formatBytes(sizeBytes)} · ${formatShortDate(receivedAt)}`}</span>
      </span>
      <button type="button" aria-label={`Download ${filename}`} className="text-muted-foreground hover:text-foreground" disabled={disabled} onClick={onDownload}>
        <DownloadSimple size={15} />
      </button>
    </li>
  );
}

function FileTypeIcon({ contentType }: { contentType: string }) {
  if (contentType === 'application/pdf') return <FilePdf size={18} />;
  if (contentType.startsWith('image/')) return <FileImage size={18} />;
  if (contentType.includes('spreadsheet') || contentType === 'text/csv') return <FileXls size={18} />;
  if (contentType.includes('word')) return <FileDoc size={18} />;
  return <FileText size={18} />;
}

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(new Date(iso));
}
